import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

@customElement('invite-banner')
export class InviteBanner extends LitElement {
  static override styles = [
    tokens,
    css`
      /* Placed by airmash-lobby's top row, in the player count's place:
         the invite matters more than the count at this moment. */
      :host {
        display: block;
        max-width: min(400px, 100%);
        pointer-events: auto;
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Blue/red palettes are single-use so kept inline; if a second
         "invite-style" banner ever appears, promote to tokens. */
      .banner {
        padding: 10px 14px;
        border-radius: var(--radius-md);
        text-align: center;
        font-size: var(--font-size-sm);
        text-shadow: var(--shadow-text);
        color: var(--color-text-white);
      }

      .banner.valid {
        background-color: rgb(68, 125, 167);
      }

      .banner.invalid {
        background-color: rgb(180, 80, 80);
      }

      .inviter {
        font-weight: bold;
        color: var(--color-text-link);
      }

      .destination {
        font-weight: bold;
      }

      /* <button> with native reset for keyboard activation + screen-reader
         semantics. 26px tall; the ::after below makes it 44px on touch. */
      .dismiss {
        position: relative;
        background-color: rgba(0, 0, 0, 0.25);
        border: none;
        font: inherit;
        text-shadow: inherit;
        display: inline-block;
        margin-left: 10px;
        padding: 6px 12px;
        font-size: var(--font-size-xs);
        color: var(--color-text-tooltip);
        cursor: pointer;
        border-radius: var(--radius-sm);
      }

      @media (hover: hover) {
        .dismiss:hover {
          color: var(--color-text-white);
          background-color: var(--color-bg-backdrop);
        }
      }

      .dismiss:active {
        color: var(--color-text-white);
        background-color: var(--color-bg-backdrop);
      }

      /* An invisible 44px tap target, inside the banner's 10px padding. */
      @media (hover: none) {
        .dismiss::after {
          content: '';
          position: absolute;
          inset: -9px 0;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  private handleDismiss = () => {
    this.api.playClick();
    this.api.clearInvite();
  };

  override render() {
    const invite = this.uiState.invite;
    if (!invite) {
      return nothing;
    }

    if (invite.isValid) {
      const inviterPart = invite.inviterName
        ? html`<span class="inviter">${invite.inviterName}</span> invited you to `
        : html`You were invited to `;

      return html`
        <div class="banner valid">
          ${inviterPart}
          <span class="destination">${invite.gameName}</span>
          <span class="destination"> in ${invite.regionName}</span>
        </div>
      `;
    }

    return html`
      <div class="banner invalid">
        Invalid invite link
        <button type="button" class="dismiss" @click=${this.handleDismiss}>dismiss</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'invite-banner': InviteBanner;
  }
}
